import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  Table,
  Spinner,
  Button,
  Modal,
  Form,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import {
  FaCheck,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaStickyNote,
  FaClipboardCheck,
  FaClipboardList,
  FaCalendarAlt,
  FaArrowLeft,
  FaUsers,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentAttendance = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionTarget, setBulkActionTarget] = useState(null);
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: '',
    variant: 'info',
  });

  useEffect(() => {
    fetchAttendance();
  }, [date]);

  const showToast = (message, variant = 'info') => {
    setToastInfo({ show: true, message, variant });
  };

  const openNoteModal = async (student) => {
    setSelectedStudent(student);
    setNoteText(''); // Reset note text while fetching
    setShowNoteModal(true);

    const token = getAuthToken();
    if (!token) {
      showToast('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-notes-v2`,
        {
          student_id: student.student_id,
          batch_id: parseInt(batchId),
          date: date,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.data?.note) {
        setNoteText(response.data.data.note);
      }
    } catch (error) {
      console.error('Error fetching note', error);
      showToast('Failed to fetch note', 'danger');
    }
  };

  const handleSaveNote = async () => {
    if (!selectedStudent) return;
    const token = getAuthToken();
    if (!token) {
      showToast('Unauthorized! Please log in.', 'danger');
      return;
    }

    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}create-student-note-v2`,
        {
          student_id: selectedStudent.student_id,
          batch_id: parseInt(batchId),
          date: date,
          note: noteText,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update `has_note` dynamically in the state
      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student.student_id === selectedStudent.student_id
            ? { ...student, has_note: noteText.trim() !== '' ? 1 : 0 }
            : student,
        ),
      );

      setShowNoteModal(false);
    } catch (error) {
      console.error('Failed to save note', error);
      showToast('Error saving note', 'danger');
    }
  };

  const generatePresentList = () => {
    const allStudents = [...boys, ...girls];
    const presents = allStudents.filter((student) => student.present);

    if (presents.length === 0) {
      showToast('No one is marked present!', 'warning');
      return;
    }

    const formattedDate = new Date(date)
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' - '); // e.g., "16 - Apr - 2025"

    const header = `*Present List : ${formattedDate}*\n*${
      batch ? `Class: ${batch}` : ''
    }*\n\n`;
    const list = presents
      .map((student, index) => `${index + 1}. ${student.name}`)
      .join('\n');

    const fullText = header + list;

    navigator.clipboard
      .writeText(fullText)
      .then(() => {
        showToast('Present list copied to clipboard!', 'success');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        showToast('Failed to copy present list.', 'danger');
      });
  };

  const generateAbsentList = () => {
    const allStudents = [...boys, ...girls];
    const absentees = allStudents.filter((student) => !student.present);

    if (absentees.length === 0) {
      showToast('Everyone is marked present!', 'warning');
      return;
    }

    const formattedDate = new Date(date)
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' - '); // e.g., "16 - Apr - 2025"

    const header = `*Absent List : ${formattedDate}*\n*${
      batch ? `Class: ${batch}` : ''
    }*\n\n`;
    const list = absentees
      .map((student, index) => `${index + 1}. ${student.name}`)
      .join('\n');

    const fullText = header + list;

    navigator.clipboard
      .writeText(fullText)
      .then(() => {
        showToast('Absent list copied to clipboard!', 'success');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        showToast('Failed to copy absent list.', 'danger');
      });
  };

  const fetchAttendance = async () => {
    setLoading(true);
    const token = getAuthToken();
    if (!token) {
      showToast('Unauthorized! Please log in.', 'danger');
      return;
    }
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-attendance-v3`,
        { batch_id: batchId, date },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBatch(response.data.batch_detail);
      setStudents(response.data.attendance);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
    setLoading(false);
  };

  const handleViewAttendance = () => {
    //const today = new Date().toISOString().slice(0, 7); // Default to the current month
    navigate(`/get-monthly-attendance?batch_id=${batchId}&date=${date}`);
  };

  const handleBulkAttendanceAction = async (status) => {
    const token = getAuthToken();
    if (!token) {
      showToast('Unauthorized! Please log in.', 'danger');
      return;
    }

    if (students.length === 0) {
      showToast('No students available to update.', 'warning');
      return;
    }

    const actionLabel = status === 1 ? 'present' : 'absent';
    const confirmed = window.confirm(
      `Mark all ${students.length} students as ${actionLabel} for ${date}?`,
    );

    if (!confirmed) {
      return;
    }

    setBulkActionLoading(true);

    try {
      const updatePromises = students.map((student) => {
        if (student.present === status) {
          return Promise.resolve();
        }

        return axios.post(
          `${appConstants.API_ROOT_URL}mark-attendance-v2`,
          {
            student_id: student.student_id,
            batch_id: batchId,
            date: date,
            time: new Date().toLocaleTimeString('en-GB'),
            present: status,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      });

      await Promise.all(updatePromises);

      setStudents((prevStudents) =>
        prevStudents.map((student) => ({
          ...student,
          present: student.present === status ? student.present : status,
        })),
      );
      setShowBulkActionModal(false);
      setBulkActionTarget(null);
      showToast(`All students marked ${actionLabel}.`, 'success');
    } catch (error) {
      console.error('Error updating bulk attendance:', error);
      showToast('Failed to update attendance for all students.', 'danger');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const markAttendance = async (index, status) => {
    const token = getAuthToken();
    if (!token) {
      showToast('Unauthorized! Please log in.', 'danger');
      return;
    }

    const student = students[index];
    const payload = {
      student_id: student.student_id,
      batch_id: batchId,
      date: date,
      time: new Date().toLocaleTimeString('en-GB'),
      present: status,
    };

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}mark-attendance-v2`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // ✅ Only update if success
      if (response.data.status === 'success') {
        setStudents((prevStudents) => {
          const updated = [...prevStudents];
          updated[index].present = status;
          return updated;
        });
      } else {
        // ❌ Backend returned an error message
        showToast(
          response.data.message || 'Failed to mark attendance.',
          'danger',
        );
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      if (error.response?.data?.message) {
        showToast(error.response.data.message, 'danger');
      } else {
        showToast('Error saving attendance. Please try again.', 'danger');
      }
    }
  };

  const changeDate = (days) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    setDate(newDate.toISOString().split('T')[0]);
  };

  const boys = students.filter((student) => student.gender === 'Male');
  const girls = students.filter((student) => student.gender === 'Female');

  const summary = {
    boysPresent: boys.filter((s) => s.present).length,
    boysAbsent: boys.length - boys.filter((s) => s.present).length,
    girlsPresent: girls.filter((s) => s.present).length,
    girlsAbsent: girls.length - girls.filter((s) => s.present).length,
  };

  const handleOpenStudentSummary = (thisStudent) => {
    navigate(`/student-summary/${thisStudent.student_id}`);
  };

  return (
    <Layout>
      <div className="p-2" style={{ backgroundColor: '#f3f3f3' }}>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 position-relative">
          {/* Title */}
          <h3 className="mb-2 mb-md-0 text-center text-md-start w-100 w-md-auto">
            {batch} Attendance
          </h3>

          {/* Centered Date Picker */}
          <div className="d-flex align-items-center justify-content-center my-2 my-md-0 w-100 w-md-auto">
            <button
              className="btn p-0 bg-transparent border-0 me-2"
              onClick={() => changeDate(-1)}
            >
              <FaChevronLeft size={20} />
            </button>
            <input
              type="date"
              className="form-control border-0 shadow-none text-center w-auto"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ minWidth: '130px' }}
            />
            <button
              className="btn p-0 bg-transparent border-0 ms-2"
              onClick={() => changeDate(1)}
            >
              <FaChevronRight size={20} />
            </button>
          </div>

          {/* Right-Aligned Icon Buttons */}
          <div className="d-flex align-items-center justify-content-center justify-content-md-end gap-2 w-100 w-md-auto mt-2 mt-md-0">
            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderRadius: '50%',
              }}
              title="Copy Absents"
              onClick={generateAbsentList}
            >
              <FaClipboardList size={16} />
            </button>

            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderRadius: '50%',
              }}
              title="Copy Presents"
              onClick={generatePresentList}
            >
              <FaClipboardCheck size={16} />
            </button>

            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderRadius: '50%',
              }}
              title="Bulk Actions"
              onClick={() => {
                setBulkActionTarget(null);
                setShowBulkActionModal(true);
              }}
            >
              <FaUsers size={16} />
            </button>

            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderRadius: '50%',
              }}
              title="Monthly View"
              onClick={handleViewAttendance}
            >
              <FaCalendarAlt size={16} />
            </button>

            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderRadius: '50%',
              }}
              title="Go Back"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft size={16} />
            </button>
          </div>
        </div>

        <table className="table table-bordered table-sm text-center mb-0 ">
          <thead className="table-secondary">
            <tr>
              <th className="p-0">Gender</th>
              <th className="p-0">Present</th>
              <th className="p-0">Absent</th>
              <th className="p-0">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-0">Boys</td>
              <td className="p-0">{summary.boysPresent}</td>
              <td className="p-0">{summary.boysAbsent}</td>
              <td className="p-0">{boys.length}</td>
            </tr>
            <tr>
              <td className="p-0">Girls</td>
              <td className="p-0">{summary.girlsPresent}</td>
              <td className="p-0">{summary.girlsAbsent}</td>
              <td className="p-0">{girls.length}</td>
            </tr>
            <tr className="fw-bold">
              <td className="p-0">Total</td>
              <td className="p-0">
                {summary.boysPresent + summary.girlsPresent}
              </td>
              <td className="p-0">
                {summary.boysAbsent + summary.girlsAbsent}
              </td>
              <td className="p-0">{students.length}</td>
            </tr>
          </tbody>
        </table>

        {loading ? (
          <p>Loading students...</p>
        ) : (
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-center fw-bold">Boys</h6>
              <table className="table table-sm table-striped text-center mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th className="p-1">#</th>
                    <th className="p-1 text-start">Name</th>
                    <th className="p-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {boys.map((student, index) => (
                    <tr key={student.student_id}>
                      <td className="p-1">{index + 1}</td>
                      <td
                        className="p-1 text-start"
                        onDoubleClick={() => {
                          handleOpenStudentSummary(student);
                        }}
                      >
                        {student.name}
                      </td>
                      <td className="p-1 d-flex justify-content-center gap-3">
                        <Button
                          variant={student.present ? 'success' : 'danger'}
                          size="sm"
                          onDoubleClick={() =>
                            markAttendance(
                              students.indexOf(student),
                              student.present ? 0 : 1,
                            )
                          }
                        >
                          {student.present ? <FaCheck /> : <FaTimes />}
                        </Button>

                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => openNoteModal(student)}
                          style={{
                            color: student.has_note ? '#e01616' : '', // Ensure text is readable
                          }}
                        >
                          <FaStickyNote />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="col-md-6">
              <h6 className="text-center fw-bold">Girls</h6>
              <table className="table table-sm table-striped text-center mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th className="p-1">#</th>
                    <th className="p-1 text-start">Name</th>
                    <th className="p-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {girls.map((student, index) => (
                    <tr key={student.student_id}>
                      <td className="p-1">{index + 1}</td>
                      <td
                        className="p-1 text-start"
                        onDoubleClick={() => {
                          handleOpenStudentSummary(student);
                        }}
                      >
                        {student.name}
                      </td>
                      <td className="p-1 d-flex justify-content-center gap-3">
                        <Button
                          variant={student.present ? 'success' : 'danger'}
                          size="sm"
                          onDoubleClick={() =>
                            markAttendance(
                              students.indexOf(student),
                              student.present ? 0 : 1,
                            )
                          }
                        >
                          {student.present ? <FaCheck /> : <FaTimes />}
                        </Button>

                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => openNoteModal(student)}
                          style={{
                            color: student.has_note ? '#e01616' : '', // Ensure text is readable
                          }}
                        >
                          <FaStickyNote />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={showBulkActionModal}
        onHide={() => setShowBulkActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk Attendance Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Choose what to do for all students on <strong>{date}</strong>.
          </p>
          <p className="text-muted mb-0">
            This will update every student in the current batch list.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBulkActionModal(false)}
            disabled={bulkActionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => handleBulkAttendanceAction(1)}
            disabled={bulkActionLoading}
          >
            {bulkActionLoading ? 'Updating...' : 'Mark All Present'}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleBulkAttendanceAction(0)}
            disabled={bulkActionLoading}
          >
            {bulkActionLoading ? 'Updating...' : 'Mark All Absent'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Note Modal */}
      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Student Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Note for {selectedStudent?.name}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveNote}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default StudentAttendance;
